import { marked } from 'marked';

marked.setOptions({ gfm: true, breaks: false, headerIds: false, mangle: false });

/**
 * Render an academic-style markdown blob into clean HTML plus an extracted
 * references list. Used by AssignmentView, AssignmentLive, and DissertationView
 * so all three pages behave identically.
 *
 * - Strips `?utm_source=…` and similar tracking parameters from every URL.
 * - Pulls inline markdown links `[label](url)` out of the prose and into a
 *   deduplicated references array. The prose keeps the (Author, Year) form.
 * - Leaves plain `(Author, Year)` citations untouched.
 * - Normalises heading/paragraph spacing so `## Heading` always produces a real
 *   <h2> (not literal "## Heading" text).
 *
 * Return shape: { html: string, references: [{ url, title }] }
 */
export function renderAcademicMarkdown(raw) {
  if (!raw || typeof raw !== 'string') {
    return { html: '', references: [] };
  }
  // Backend stores chapter content as a single flat string with zero
  // newlines. Paragraph breaks are double-spaces, and section headings are
  // `  ## N.N Title  body…`. Expand that shape into real blank lines BEFORE
  // extractCitations' whitespace tidier collapses all double-spaces to a
  // single space and hides the structure from us.
  const expanded = expandFlatBlob(raw);
  const { body, references } = extractCitations(expanded);
  const prepped = normalise(body);
  try {
    return { html: marked.parse(prepped), references };
  } catch {
    return {
      html: escapeHtml(raw).replace(/\n/g, '<br/>'),
      references: [],
    };
  }
}

// Turn a no-newlines, double-space-delimited chapter blob into proper
// paragraph-separated markdown. Safe to call on already-formatted content:
// only fires when the input has no `\n\n` at all.
// Parse a cite body like "Bhaskar2016" or "A2020■B2021" or "A2020, B2021"
// into a human-readable APA-ish parenthetical "(Author, Year; …)".
function formatCiteBody(inner) {
  if (!inner) return '';
  const keys = String(inner)
    .split(/[-■□▪▫◻◼,;]+/u)
    .map((s) => s.trim())
    .filter(Boolean);
  const formatted = keys.map((key) => {
    const yr = key.match(/(\d{4}[a-z]?)$/);
    if (yr) {
      const author = key
        .slice(0, key.length - yr[0].length)
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .trim();
      return author ? `${author}, ${yr[0]}` : yr[0];
    }
    return key;
  });
  return formatted.length ? `(${formatted.join('; ')})` : '';
}

function expandFlatBlob(raw) {
  if (/\n\n/.test(raw)) return raw;
  if (!/\s{2,}/.test(raw)) return raw;
  let out = raw;
  // Blank line before every `## N.N` heading
  out = out.replace(/\s{2,}(#{1,6}\s+\d+(?:\.\d+)*\s+)/g, '\n\n$1');
  // Split title from body on each heading line. Title is the first 2-8 words
  // of Title Case after the section number; everything after that — even on
  // the same line — moves to the body. The previous "first double-space"
  // heuristic let an entire opening sentence be promoted to a giant heading
  // (e.g. "## 3.11 Chapter 3 set out an econometric protocol… (World Bank,
  // 2024; Bank of Zambia, 2022)." rendered as one wall of bold text).
  out = out.replace(
    /^(#{1,6})\s+(\d+(?:\.\d+)*)\s+([^\n]+)$/gm,
    (full, hashes, num, rest) => {
      // Prefer splitting at a literal double-space if one exists in the line
      const dsMatch = rest.match(/^([^\n]+?)\s{2,}([^\n]+)$/);
      if (dsMatch) {
        const titleWords = dsMatch[1].split(/\s+/);
        if (titleWords.length <= 8) {
          return `${hashes} ${num} ${dsMatch[1]}\n\n${dsMatch[2]}`;
        }
        // Title overflowed 8 words — keep first 8 as title, push rest to body
        const title = titleWords.slice(0, 8).join(' ');
        const overflow = titleWords.slice(8).join(' ');
        return `${hashes} ${num} ${title}\n\n${overflow} ${dsMatch[2]}`;
      }
      // No double-space gap — cap the heading at 8 words anyway so a missing
      // blank-line break can't run a whole sentence into the heading.
      const words = rest.split(/\s+/);
      if (words.length <= 8) return full;
      const title = words.slice(0, 8).join(' ');
      const overflow = words.slice(8).join(' ');
      return `${hashes} ${num} ${title}\n\n${overflow}`;
    },
  );
  // Group sentences into paragraphs. The AI emits double-spaces between
  // sentences (not paragraphs) in flat-blob content, so a naive
  // `[ \t]{2,}/g → \n\n` turns every sentence into its own one-line
  // paragraph. Walk each chunk between existing `\n\n` (heading splits
  // from above), split on double-spaces to get sentences, then group
  // ~4 sentences per paragraph for readable prose blocks.
  const SENTENCES_PER_PARA = 4;
  out = out
    .split(/\n{2,}/)
    .map((chunk) => {
      const trimmed = chunk.trim();
      if (!trimmed) return '';
      // Headings keep their structure
      if (/^#{1,6}\s/.test(trimmed)) return trimmed;
      const sentences = trimmed
        .split(/[ \t]{2,}/)
        .map((s) => s.trim())
        .filter(Boolean);
      if (sentences.length <= 1) return trimmed;
      const paragraphs = [];
      for (let i = 0; i < sentences.length; i += SENTENCES_PER_PARA) {
        paragraphs.push(sentences.slice(i, i + SENTENCES_PER_PARA).join(' '));
      }
      return paragraphs.join('\n\n');
    })
    .filter(Boolean)
    .join('\n\n');
  return out;
}

function extractCitations(raw) {
  let body = String(raw).replace(/\r\n/g, '\n');

  // LaTeX-style citation markers that came through with braces mangled into
  // unicode black-squares (U+25A0 "■" / U+25FB etc. — the model's tokenizer
  // sometimes emits these instead of `{` and `}`). Convert
  //   ■cite■AuthorYear■           →   (Author, Year)
  //   ■cite■A2020■B2021■          →   (A, 2020; B, 2021)
  //   \cite{AuthorYear}           →   (Author, Year)
  // so the reader never sees raw boxes. Runs before URL extraction so the
  // parenthesised result doesn't get eaten by later patterns.
  // Citation sentinels the model emits fall into two ranges:
  //   • Unicode Private Use Area codepoints U+E200..U+E20F (the actual
  //     characters stored in RTDB for this model — they render as empty
  //     glyphs / black squares in fonts that don't cover PUA).
  //   • U+25A0 ■ / U+25A1 □ / U+25AA ▪ / U+25AB ▫ / U+25FB ◻ / U+25FC ◼
  //     (fallback — older exports where the tokenizer dropped into these).
  const CITE_MARK = '[\\uE200-\\uE2FF■□▪▫◻◼]';
  body = body.replace(
    new RegExp(`${CITE_MARK}cite${CITE_MARK}([\\s\\S]+?)${CITE_MARK}(?=[\\s.,;:)!?]|$)`, 'gu'),
    (_m, inner) => formatCiteBody(inner),
  );
  body = body.replace(/\\cite\{([^}]+)\}/g, (_m, inner) => formatCiteBody(inner));

  // Inline `[TABLE:xxx]` / `[CHART:xxx]` / `[FIGURE:xxx]` markers are
  // scaffolding the AI leaves behind to point at tables or charts that get
  // rendered separately below the chapter. Strip them from the running prose
  // so the reader doesn't see `[TABLE:wb_fd7fd8ee-659_tbl2]` inline.
  body = body.replace(/\s*\[(?:TABLE|CHART|FIGURE)(?::[^\]]+)?\]/gi, '');

  // "Paragraphs omitted (analysis phase)." and similar filler that leaks
  // from draft templates.
  body = body.replace(/\bParagraphs? omitted\s*\([^)]*\)\.?\s*/gi, '');

  // Mop-up: stray PUA / box sentinels not caught by the cite-marker regex
  // (e.g. `macro■data` where a dash/comma got mangled to a sentinel).
  // Replace with a space so the prose reads naturally.
  body = body.replace(/[-■□▪▫◻◼]/gu, ' ');

  // Strip tracking query params from any URL
  body = body.replace(/([?&])utm_[a-z_]+=[^\s)&\]]+/gi, (_m, sep) => (sep === '?' ? '' : ''));
  // Strip any remaining `?utm_source=...` that ended up at URL tail
  body = body.replace(/\?utm_[a-z_]+=[^\s)&\]]+/gi, '');

  const refs = [];
  const seen = new Set();
  const push = (url, label) => {
    try {
      const u = new URL(url);
      const clean = u.origin + u.pathname;
      if (seen.has(clean)) return;
      seen.add(clean);
      refs.push({
        url: clean,
        title:
          label && !/^https?:/.test(label) && label !== u.hostname
            ? label
            : u.hostname,
      });
    } catch {
      /* malformed URL — drop */
    }
  };

  // (Author, Year) ([label](url))  →  (Author, Year)
  body = body.replace(
    /(\([^)]+?\d{4}[a-z]?\))\s*\(\s*\[([^\]]+)\]\(([^)]+)\)\s*\)/g,
    (_m, cite, label, url) => {
      push(url, label);
      return cite;
    },
  );
  // Standalone ([label](url))
  body = body.replace(/\(\s*\[([^\]]+)\]\(([^)]+)\)\s*\)/g, (_m, label, url) => {
    push(url, label);
    return '';
  });
  // [label](url) inline → keep label, URL goes to refs
  body = body.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, (_m, label, url) => {
    push(url, label);
    return label;
  });
  // Bare (https://...) in parens
  body = body.replace(/\((?:see\s+)?https?:\/\/\S+?\)/gi, (m) => {
    const url = m.replace(/^\((?:see\s+)?/, '').replace(/\)$/, '');
    push(url, null);
    return '';
  });

  // Tidy leftovers
  body = body
    .replace(/[ \t]+([.,;:!?])/g, '$1')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n\s*\n\s*\n+/g, '\n\n');
  return { body, references: refs };
}

// Common section labels that should always render as real headings, not as
// run-in bold text at the start of a paragraph. Order matters — longer
// multi-word labels come first so they match before their shorter prefixes.
const SECTION_LABELS = [
  'Literature Review',
  'Theoretical Framework',
  'Conceptual Framework',
  'Research Methodology',
  'Research Questions',
  'Research Objectives',
  'Research Design',
  'Data Analysis',
  'Data Collection',
  'Findings and Analysis',
  'Findings',
  'Analysis',
  'Results',
  'Discussion',
  'Discussion of Findings',
  'Recommendations',
  'Implications',
  'Limitations',
  'Future Research',
  'Conclusion',
  'Conclusions',
  'References',
  'Bibliography',
  'Abstract',
  'Introduction',
  'Background',
  'Methodology',
  'Methods',
  'Summary',
  'Appendix',
  'Hypothesis',
  'Hypotheses',
  'Problem Statement',
  'Significance',
  'Scope',
  'Definition of Terms',
  'Acknowledgements',
];

// Only promote when the label is followed by explicit heading punctuation
// (`:`, `-`, `–`, `—`). The previous `\s+(?=[A-Z])` branch mis-fired on
// ordinary prose like "Summary Statistics", "Analysis Shows", "Results
// Indicate", chopping every such paragraph into a tiny heading + stray
// sentence — which made 1.9-2.4 read as bullet notes rather than
// paragraphs. If the AI forgets the newline after a bare section label,
// that's now a generator bug to fix upstream, not a render-time guess.
const SECTION_LABEL_RE = new RegExp(
  String.raw`^(?:\*\*\s*)?(${SECTION_LABELS.join('|')})\s*(?:\*\*)?\s*[:\-–—]\s+`,
  'i',
);

function promoteRunInHeadings(text) {
  // Walk paragraph-by-paragraph. For each paragraph whose first token matches
  // a known section label (optionally wrapped in ** or followed by : / -), we
  // extract the label into its own `## Heading` line and keep the rest as
  // the paragraph body. Safe: only promotes the FIRST line of a paragraph,
  // never mid-paragraph.
  const paragraphs = text.split(/\n{2,}/);
  return paragraphs
    .map((p) => {
      const trimmed = p.trimStart();
      // Skip paragraphs that already begin with a heading or list marker
      if (/^#{1,6}\s/.test(trimmed) || /^(-|\*|\d+\.)\s/.test(trimmed) || /^>\s/.test(trimmed)) {
        return p;
      }
      const m = SECTION_LABEL_RE.exec(trimmed);
      if (!m) return p;
      const label = m[1];
      const rest = trimmed.slice(m[0].length).trimStart();
      // Title-case the label so `introduction` → `Introduction`
      const titled = label.replace(/\w\S*/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());
      return rest ? `## ${titled}\n\n${rest}` : `## ${titled}`;
    })
    .join('\n\n');
}

function regroupSentenceParagraphs(text) {
  // Older dissertations were stored with the legacy expansion that turned
  // every double-space into `\n\n`, so each sentence ended up on its own
  // line. Detect that pattern at render time and regroup.
  const paragraphs = text.split(/\n{2,}/).map((p) => p.trim());
  if (paragraphs.length < 4) return text;
  const proseParas = paragraphs.filter(
    (p) =>
      p &&
      !/^#{1,6}\s/.test(p) &&
      !/^(?:-|\*|\d+\.)\s/.test(p) &&
      !/^>\s/.test(p) &&
      !/^!\[/.test(p) &&
      !/^\|/.test(p),
  );
  if (proseParas.length < 4) return text;
  const oneSentenceCount = proseParas.filter((p) => {
    const breaks = (p.match(/[.!?]["')\]]?\s+[A-Z(]/g) || []).length;
    return breaks === 0;
  }).length;
  // Only regroup when MOST prose paragraphs are single-sentence — that's the
  // tell-tale sign of legacy fragmentation. If the AI is already writing
  // proper multi-sentence paragraphs, leave them alone.
  if (oneSentenceCount / proseParas.length < 0.6) return text;
  const SENTENCES_PER_PARA = 4;
  const result = [];
  let buffer = [];
  const flush = () => {
    if (buffer.length) {
      result.push(buffer.join(' '));
      buffer = [];
    }
  };
  for (const p of paragraphs) {
    if (!p) {
      flush();
      continue;
    }
    if (
      /^#{1,6}\s/.test(p) ||
      /^(?:-|\*|\d+\.)\s/.test(p) ||
      /^>\s/.test(p) ||
      /^!\[/.test(p) ||
      /^\|/.test(p)
    ) {
      flush();
      result.push(p);
      continue;
    }
    buffer.push(p);
    const accumulated = buffer.join(' ');
    const sentenceCount = (accumulated.match(/[.!?]["')\]]?\s+[A-Z(]/g) || []).length + 1;
    if (sentenceCount >= SENTENCES_PER_PARA) flush();
  }
  flush();
  return result.join('\n\n');
}

function normalise(s) {
  let out = s.replace(/\r\n/g, '\n');

  // Insert blank line before any heading that ran on inline
  out = out.replace(/([^\n])\n(#{1,6}\s)/g, '$1\n\n$2');
  // Ensure blank line before a heading that appears after content on same line
  out = out.replace(/([^\n ])\s+(#{1,6}\s+)/g, '$1\n\n$2');

  // Strip duplicate chapter/section headings at the very top — the view
  // draws the title separately.
  out = out.replace(/^\s*#{1,6}\s*Chapter\s+\d+[^\n]*\n+/i, '');
  out = out.replace(/^\s*Chapter\s+\d+\s*[:\-–—][^\n]*\n+/i, '');
  out = out.replace(/^\s*#{1,6}\s*\d+(?:\.\d+)*\s+[^\n]+\n+/, '');
  out = out.replace(/^\s*\*\*\s*\d+(?:\.\d+)*\s+[^*\n]+\*\*\s*\n+/, '');

  // One-giant-blob heuristic: if there are still no blank lines but prose
  // has sentences separated by ". ", break at those boundaries.
  if (!/\n\n/.test(out) && /\.\s{2,}[A-Z]/.test(out)) {
    out = out.replace(/\.\s{2,}([A-Z])/g, '.\n\n$1');
  }

  // Demote numbered-list-style methodology paragraphs back to prose. The
  // AI sometimes ignores the "no numbered lists" rule and writes
  //   1. Data strategy. Annual observations …
  //   2. Variable screening. Each series is …
  // even though the prompt forbids it. Only demote when the second token
  // looks like a "lead phrase" — a short Title-Case clause ending in a
  // period — i.e. it reads as a paragraph lead, not a bullet item.
  // Promote that lead phrase to bold so the paragraph still has visual
  // structure.
  // Trailing `( +)` (literal spaces only, NOT `\s+`) so we don't munge
  // legitimate numbered lists in Sections 1.4/1.5 where the item ends with
  // a period followed by `\n\n` — those keep their numbering.
  out = out.replace(
    /^(\s*)(\d{1,2})\.\s+(\*\*([^*\n]+?)\*\*\.?|([A-Z][\w][^.\n]{0,70}?)\.)( +)(?=\S)/gm,
    (_full, indent, _num, _all, alreadyBold, plainLead, gap) => {
      const lead = alreadyBold
        ? `**${alreadyBold.trim()}.**`
        : `**${plainLead.trim()}.**`;
      return `${indent}${lead}${gap}`;
    },
  );
  // Also handle the case where the AI emits `N. Title.` on its OWN line
  // followed by the body in the next paragraph:
  //   `1. Visual and descriptive pre-checks.`
  //   `Before invoking any formal tests, both series were plotted…`
  // Negative lookahead `(?!\d+\.\s)` ensures we only demote when the next
  // paragraph is body prose, NOT another numbered item — that way
  // legitimate numbered lists in Sections 1.4/1.5 stay numbered.
  out = out.replace(
    /^(\s*)(\d{1,2})\.\s+([A-Z][\w][^.\n]{1,80}?)\.\s*$\n\n(?!\d+\.\s)([^\n#].*)/gm,
    (_full, indent, _num, lead, nextPara) => `${indent}**${lead.trim()}.** ${nextPara}`,
  );

  // Promote run-in section labels ("Introduction Rolling blackouts…",
  // "**Background** The country…", "Conclusion: …") to real headings so
  // they render as sections, not as a bold word glued to the paragraph.
  out = promoteRunInHeadings(out);

  // Oversized-heading defense: the AI sometimes prefixes a multi-sentence
  // paragraph with `## ` (or deeper), which marked turns into one giant
  // heading styled with our primary colour + border. Split at the first
  // sentence boundary: keep a short headline, push the rest to a new
  // paragraph. Heuristic: heading line longer than 12 words or 110 chars.
  out = out.replace(/^(#{1,6})\s+([^\n]+)/gm, (full, hashes, line) => {
    const trimmed = line.trim();
    const wordCount = trimmed.split(/\s+/).filter(Boolean).length;
    if (wordCount <= 12 && trimmed.length <= 110) return full;
    // Try to split at the first sentence boundary ". " or ": "
    const sentenceEnd = trimmed.search(/[.!?:]\s+[A-Z]/);
    if (sentenceEnd > 4 && sentenceEnd < trimmed.length - 8) {
      const head = trimmed.slice(0, sentenceEnd + 1).trim();
      const rest = trimmed.slice(sentenceEnd + 1).trim();
      return `${hashes} ${head}\n\n${rest}`;
    }
    // No sentence boundary — keep first ~8 words as a short heading, rest as body
    const words = trimmed.split(/\s+/);
    const headWords = Math.min(8, Math.floor(words.length / 3) || 6);
    const head = words.slice(0, headWords).join(' ').replace(/[,;:]$/, '');
    const rest = words.slice(headWords).join(' ');
    return rest
      ? `${hashes} ${head}\n\n${rest}`
      : `${hashes} ${head}`;
  });

  // Regroup single-sentence paragraphs into multi-sentence paragraphs.
  // Handles legacy content stored with `\n\n` between every sentence.
  out = regroupSentenceParagraphs(out);

  return out.trimStart();
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
